import { Trans, useLingui } from '@lingui/react/macro';
import { Copy, Download, RotateCcw, ToggleLeft, ToggleRight, Trash2, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { TemplateStatus } from '@/types/saved-template';

/**
 * Bulk-action bar that appears above the templates table once at least one
 * row is selected (Task 6.13, A7/D37). Renders nothing when `count` is zero
 * so the parent can mount it unconditionally without a wrapping `&&`.
 *
 * The bar is context-aware: an action shows only when it applies to *every*
 * selected template, mirroring the per-row menu.
 *   - Export and Clear: always.
 *   - Duplicate and Delete: when no selected row is deleted.
 *   - Publish, Unpublish and Restore: only for a uniform draft, published
 *     or deleted selection, respectively.
 * So a selection that mixes deleted with anything else shows just the
 * universal common actions.
 */
export function TemplatesBulkActions({
  count,
  selectedStatuses,
  onBulkDelete,
  onBulkDuplicate,
  onBulkExport,
  onBulkPublish,
  onBulkUnpublish,
  onBulkRestore,
  onClear,
}: TemplatesBulkActionsProps) {
  const { t } = useLingui();
  if (count === 0) return null;

  // An action shows when it applies to *every* selected template. Duplicate
  // and Delete are valid for any live (non-deleted) template, so they stay
  // for a draft+published mix. Publish / Unpublish / Restore are
  // single-status actions, so they only show for a uniform selection.
  const uniqueStatuses = Array.from(new Set(selectedStatuses));
  const onlyStatus = uniqueStatuses.length === 1 ? uniqueStatuses[0] : null;
  const liveOnly = uniqueStatuses.length > 0 && !uniqueStatuses.includes('deleted');

  const canPublish = onlyStatus === 'draft';
  const canUnpublish = onlyStatus === 'published';
  const canRestore = onlyStatus === 'deleted';
  const canDuplicate = liveOnly;
  const canDelete = liveOnly;

  return (
    <div className="bg-muted/50 flex items-center gap-3 rounded-md border p-2">
      <span className="text-sm font-medium">
        <Trans>{count} selected</Trans>
      </span>
      <div className="ml-auto flex flex-wrap justify-end gap-2">
        {canPublish && (
          <Button variant="outline" size="sm" onClick={onBulkPublish}>
            <ToggleRight className="mr-2 h-4 w-4" /> <Trans>Publish</Trans>
          </Button>
        )}
        {canUnpublish && (
          <Button variant="outline" size="sm" onClick={onBulkUnpublish}>
            <ToggleLeft className="mr-2 h-4 w-4" /> <Trans>Unpublish</Trans>
          </Button>
        )}
        {canRestore && (
          <Button variant="outline" size="sm" onClick={onBulkRestore}>
            <RotateCcw className="mr-2 h-4 w-4" /> <Trans>Restore</Trans>
          </Button>
        )}
        {canDuplicate && (
          <Button variant="outline" size="sm" onClick={onBulkDuplicate}>
            <Copy className="mr-2 h-4 w-4" /> <Trans>Duplicate</Trans>
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={onBulkExport}>
          <Download className="mr-2 h-4 w-4" /> <Trans>Export</Trans>
        </Button>
        {canDelete && (
          <Button variant="destructive" size="sm" onClick={onBulkDelete}>
            <Trash2 className="mr-2 h-4 w-4" /> <Trans>Delete</Trans>
          </Button>
        )}
        <Button variant="ghost" size="icon" aria-label={t`Clear selection`} onClick={onClear}>
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

interface TemplatesBulkActionsProps {
  count: number;
  /** Distinct statuses present in the current selection; drives which lifecycle actions show. */
  selectedStatuses: TemplateStatus[];
  onBulkDelete: () => void;
  onBulkDuplicate: () => void;
  onBulkExport: () => void;
  onBulkPublish: () => void;
  onBulkUnpublish: () => void;
  onBulkRestore: () => void;
  onClear: () => void;
}
